import {
  DurationTable,
  FrequencyTable,
  IntervalTable,
  RateTable,
  TaskAnalysisTable,
} from 'src/modules/session/components';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Text, TouchableOpacity, View, Animated, Platform} from 'react-native';
import ViewShot from 'react-native-view-shot';
import AntDesign from 'react-native-vector-icons/AntDesign';
import {ProgramMethodAvatar} from 'src/modules/programs/components';
import {IconButton, Snackbar} from 'react-native-paper';
import {Colors, Typography} from 'src/styles';
import Collapsible from 'react-native-collapsible';
import {ProgramProfile} from 'src/modules';
import {useNavigation} from '@react-navigation/core';
import {collectionMethods} from 'src/modules/programs/components/program-card/collection';
import {compose} from 'recompose';
import {withDatabase} from '@nozbe/watermelondb/react';
import {useDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import {Q} from '@nozbe/watermelondb';
import _ from 'lodash';
import moment from 'moment';
import {of} from 'rxjs';
import {intervalTypes} from 'src/hook-form-inputs/interval-type';
import {
  DurationProgressGraph,
  FrequencyProgressGraph,
  IntervalProgressGraph,
  TaskAnalysisProgressGraph,
  TrialByTrialProgress,
  RateProgressGraph,
} from 'src/modules/reports/components';
import {Menu, Modal} from 'src/design-system';
import {
  exportDuration,
  exportFrequency,
  exportInterval,
  exportRate,
  exportTaskAnalysis,
} from 'src/common-utils/program-evaluation';
import {exportCsv} from 'src/common-utils/export-csv';
import {exportGraph} from 'src/common-utils/export-graph';
import ProgramMenu from 'src/design-system/program-menu';
import ProgramInstructionModal from '../program-instruction-modal';
import ProgramProgress from 'src/modules/programs/components/program-progress-legacy';
import {useStyle} from 'src/providers/style';
import {EnvironmentalFactor} from 'src/models';

const isWeb = Platform.OS === 'web';

const SessionProgramDataTable = ({
  program,
  patient,
  sets,
  targets,
  totalEvents,
  envs,
  session,
  showGraphs = true,
  recentEvents,
  recentSets,
  analyzedSets,
  analyzedEvents,
  programSessions,
  showProgramMenu = false,
  reorderPrograms = () => {},
  analysis,
  useObservables = false,
}: any) => {
  const styles = useStyle();
  const database = useDatabase();
  const navigation = useNavigation();

  const graphExportRef = useRef();
  const [animatedValue] = useState(new Animated.Value(0));
  const recentSet = recentSets[0];
  const recentEvent = recentEvents[0];
  const handleAnimation = useCallback(() => {
    Animated.timing(animatedValue, {
      toValue: 1,
      duration: 500,
      useNativeDriver: false,
    }).start(() => {
      Animated.timing(animatedValue, {
        toValue: 0,
        duration: 5000,
        useNativeDriver: false,
      }).start();
    });
  }, [animatedValue]);

  const [latestEventId, setLatestEventId] = useState('');
  const [latestSetId, setLatestSetId] = useState('');
  const [showExportDlg, setShowExportDlg] = useState<string | undefined>(
    undefined,
  );
  const [instructionsModal, setInstructionsModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const sessionSets = useObservables
    ? recentSets
    : analyzedSets.filter((set: any) => set.program_id === program.id);
  const setIds = sessionSets.map((set: any) => set.id);
  const viewHistory = () => {
    setHistoryModal(!historyModal);
  };
  const viewInstructions = () => {
    setInstructionsModal(!instructionsModal);
  };
  const filteredEnvs = useMemo(
    () =>
      envs?.filter(
        (item: EnvironmentalFactor) =>
          moment(item?.date).format('YYYY-MM-DD') === session?.sessionDate,
      ),
    [envs, session],
  );

  useEffect(() => {
    if (recentEvent) {
      const eventDurationInSeconds = moment(new Date()).diff(
        recentEvent.updatedAt,
        'seconds',
      );
      if (eventDurationInSeconds <= 20 && recentEvent.id !== latestEventId) {
        setLatestEventId(recentEvent.id);
        handleAnimation();
        reorderPrograms(program);
      }
    }
    if (recentSet) {
      const setDurationInSeconds = moment(new Date()).diff(
        recentSet.updatedAt,
        'seconds',
      );
      if (setDurationInSeconds <= 20 && recentSet.id !== latestSetId) {
        setLatestSetId(recentSet.id);
        handleAnimation();
        reorderPrograms(program);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEvent, recentSet]);

  const borderInterpolation = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [Colors.RAVEN_WHITE, Colors.SECONDARY_600],
  });

  const animatedStyle = {
    shadowColor: borderInterpolation,
    borderColor: borderInterpolation,
    shadowOffset: {
      height: animatedValue,
      width: animatedValue,
    },
    shadowOpacity: animatedValue,
    borderWidth: 2,
  };

  const [collapsed, setCollapsed] = useState(Platform.OS !== 'web');
  const [currentTarget, setCurrentTarget] = useState(targets?.[0]?.id);
  const method = collectionMethods.find(
    collectionMethod => collectionMethod.value === program?.method,
  );

  const analyzedProgram = () => {
    const analysisState: any = [];
    const programAnalysis: any = {};
    const analyzedSessionSets = _.groupBy(sets, 'session.id');
    const eventsBySetId = _.groupBy(totalEvents, 'set.id');
    switch (program?.method) {
      case 'task_analysis':
        for (const sessionId of Object.keys(analyzedSessionSets)) {
          let collectorIds: string[] = [];
          const sessionSet = analyzedSessionSets[sessionId];
          const average = Math.ceil(
            sessionSet.reduce((totalSum: number, set: any) => {
              const setEvents = eventsBySetId[set?.id];
              let eventCollectors = _.uniq(
                _.map(setEvents, (event: any) => event?.createdBy),
              ).filter((id: string) => id !== '');
              collectorIds.push(...eventCollectors);
              return (
                totalSum +
                Math.ceil(
                  ((setEvents?.filter((event: any) => event.value === 'success')
                    .length || 0) /
                    (setEvents?.length || 1)) *
                    100,
                )
              );
            }, 0) / sessionSet.length,
          );
          collectorIds = _.uniq(collectorIds);

          const sessionObject = {
            sessionId,
            average,
            timestamp: sessionSet[0]?.createdAt,
            collectors: collectorIds,
            state: program?.phase,
          };

          analysisState.push(sessionObject);
          programAnalysis[sessionId] = sessionObject;
        }
        break;
      case 'frequency':
        for (const {id: sessionId} of programSessions) {
          const set = analyzedSessionSets[sessionId]?.[0];
          const frequencySetIds = _.map(analyzedSessionSets[sessionId], 'id');
          const setEvents = totalEvents.filter((event: any) =>
            frequencySetIds.includes(event?.setId),
          );
          const sum = setEvents?.length || 0;
          const eventCollectorIds = _.uniq(
            _.map(setEvents, (event: any) => event?.createdBy),
          ).filter((id: string) => id !== '');
          const setCollectorIds = eventCollectorIds.length
            ? eventCollectorIds
            : [set?.createdBy];
          const sessionObject = {
            sessionId,
            average: sum,
            timestamp: set?.createdAt,
            collectors: setCollectorIds,
            state: program?.phase,
          };

          analysisState.push(sessionObject);
          programAnalysis[sessionId] = sessionObject;
        }
        break;
      case 'interval':
        for (const {id: sessionId} of programSessions) {
          let collectorIds: string[] = [];
          const sessionSet = analyzedSessionSets[sessionId];
          const average =
            sessionSet.reduce((a: number, b: Set) => {
              const setEvents = eventsBySetId[b.id] || [];
              let eventCollectors = _.uniq(
                _.map(setEvents, (event: any) => event?.createdBy),
              );
              collectorIds.push(...eventCollectors);
              return (
                a +
                setEvents.reduce((setPercentage: number, event: any) => {
                  return setPercentage + (event.value === 'Y' ? 1 : 0) * 100;
                }, 0) /
                  (setEvents.length || 1)
              );
            }, 0) / sessionSet.length;
          collectorIds = _.uniq(collectorIds).filter((id: string) => id !== '');
          const sessionObject = {
            sessionId,
            average: average,
            timestamp: sessionSet[0]?.createdAt,
            collectors: collectorIds,
            state: program?.phase,
          };

          analysisState.push(sessionObject);
          programAnalysis[sessionId] = sessionObject;
        }
        break;
      case 'rate':
        for (const {
          id: sessionId,
          startTimestamp,
          endTimestamp,
          userId,
        } of programSessions) {
          const set = analyzedSessionSets[sessionId]?.[0];
          const setEvents = eventsBySetId[set?.id];
          const rateEvents = setEvents?.length || 0;
          const setCollectorIds = _.uniq(
            _.map(setEvents, (event: any) => event?.createdBy),
          ).filter((id: string) => id !== '');
          const collectorIds = setCollectorIds.length
            ? setCollectorIds
            : [userId];
          const duration =
            analyzedSessionSets[sessionId]?.reduce((a: number, b: any) => {
              return (
                a +
                moment
                  .duration(
                    moment(
                      !b.endTimestamp ? moment(endTimestamp) : b.endTimestamp,
                    ).diff(moment(b.startTimestamp)),
                  )
                  .asMinutes()
              );
            }, 0) || 0.001;

          let denominator = duration;

          switch (program?.unit) {
            case 'per_min':
              denominator = duration;
              break;
            case 'per_hour':
              denominator = duration / 60;
              break;
            case 'per_session':
              denominator = 1;
              break;
          }

          const rate = rateEvents / denominator;

          const sessionObject = {
            sessionId,
            sessionDate: startTimestamp,
            average: rate,
            duration: duration,
            collectors: collectorIds,
            timestamp: startTimestamp,
            state: program?.phase,
          };

          analysisState.push(sessionObject);
          programAnalysis[sessionId] = sessionObject;
        }
        break;
      case 'duration':
        for (const {id: sessionId, endTimestamp} of programSessions) {
          let setCollectorIds: string[] = [];
          const sum =
            analyzedSessionSets[sessionId]?.reduce((a: number, b: any) => {
              setCollectorIds.push(b?.createdBy);
              return (
                a +
                moment
                  .duration(
                    moment(
                      !b.endTimestamp ? moment(endTimestamp) : b.endTimestamp,
                    ).diff(moment(b.startTimestamp)),
                  )
                  .asSeconds()
              );
            }, 0) || 0;
          setCollectorIds = _.uniq(setCollectorIds).filter(
            (id: string) => id !== '',
          );
          const sessionObject = {
            sessionId,
            average: sum,
            state: program?.phase,
            timestamp: endTimestamp,
            collectors: setCollectorIds,
          };

          analysisState.push(sessionObject);
          programAnalysis[sessionId] = sessionObject;
        }
        break;
    }
    return {
      id: program.id,
      name: program.name,
      type: program.type,
      method: program.method,
      baseline: program.baseline,
      baselineCorrectProbes: program.baselineCorrectProbes,
      baselineProbes: program.baselineProbes,
      masteryValue: program.masteryValue,
      maintenance: program.maintenance,
      maintenanceValue: program.maintenanceValue,
      analysis: programAnalysis,
      analysisState: programAnalysis[session?.id],
    };
  };
  const exportPDF = async () => {
    await exportGraph(graphExportRef, program?.name, 'pdf');
    setShowExportDlg(`${program?.name}.pdf`);
  };

  const exportJpeg = async () => {
    await exportGraph(graphExportRef, program?.name, 'jpg');
    setShowExportDlg(`${program?.name}.jpg`);
  };

  const exportPng = async () => {
    await exportGraph(graphExportRef, program?.name, 'png');
    setShowExportDlg(`${program?.name}.png`);
  };

  const createCSVFile = async () => {
    let data: any[] = [];
    switch (program.method) {
      case 'duration':
        data = await exportDuration({
          sets,
          session,
        });
        break;
      case 'frequency':
        const exportUseObs = useObservables || (recentSets?.length ?? 0) > 0;
        const recapSets = exportUseObs
          ? recentSets
          : analyzedSets.filter(set => set.program_id === program.id);

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const setIds = recapSets.map(s => s.id).filter(Boolean);

        // When observables: fetch DB events (shape: e.set.id)
        const obsEvents = exportUseObs
          ? await database
              .get('events')
              .query(
                Q.where('set_id', Q.oneOf(setIds)),
                Q.where('deleted_at', null),
                Q.sortBy('created_at', Q.asc),
              )
              .fetch()
          : [];

        // When NOT observables: filter HTTP events (shape: e.set_id)
        const httpEvents = exportUseObs
          ? []
          : (analyzedEvents || []).filter(e => setIds.includes(e.set_id));

        data = await exportFrequency({
          sets: recapSets,
          events: obsEvents, // DB events if we’re in obs mode
          tableEvents: httpEvents, // HTTP events if we’re not
          session,
        });
        break;
      case 'interval':
        data = await exportInterval({
          session,
          sets:
            useObservables || recentSets?.length > 0
              ? recentSets
              : analyzedSets.filter(set => set.program_id === program.id),
          events:
            useObservables || recentSets?.length > 0
              ? recentEvents
              : analyzedEvents,
          useObservables:
            useObservables || recentSets?.length > 0 ? true : false,
        });
        break;
      case 'rate':
        const recapRateSets = useObservables
          ? recentSets
          : analyzedSets.filter(s => s.program_id === program.id);

        const setRateIds = recapRateSets.map(s => s.id).filter(Boolean);

        // Match RateTable’s withObservables HOC: pull DB events for those sets
        const dbEvents = await database
          .get('events')
          .query(
            Q.where('set_id', Q.oneOf(setRateIds)),
            Q.where('deleted_at', null),
            Q.sortBy('created_at', Q.desc),
          )
          .fetch();

        data = await exportRate({
          sets: recapRateSets,
          queriedEvents: dbEvents, // <-- key bit for Session Recap
          program,
          session, // so Date column matches the UI
        });
        break;
      case 'task_analysis':
        data = await exportTaskAnalysis({
          sets: sessionSets,
          targets,
          useObservables,
          session,
          // When using observables, fetch events from DB like the HOC does:
          events: useObservables
            ? await database
                .get('events')
                .query(
                  Q.where('set_id', Q.oneOf(setIds)),
                  Q.where('deleted_at', null),
                  Q.sortBy('created_at', Q.desc),
                )
                .fetch()
            : [],
          // When NOT using observables, pass the pre-fetched/analyzed events:
          taskEvents: useObservables
            ? []
            : analyzedEvents.filter(e => setIds.includes(e.set_id)),
        });
        break;
    }

    await exportCsv(
      `${patient?.lastName}_${program?.name}_${moment(
        session?.sessionDate,
      ).format('YYYY-MM-DD')}`,
      data,
    );
  };

  const options = [
    {
      onPress: exportJpeg,
      title: 'JPEG',
      icon: 'file-jpg-box',
    },
    {
      onPress: exportPng,
      title: 'PNG',
      icon: 'file-png-box',
    },
  ];

  if (isWeb) {
    options.unshift({
      onPress: exportPDF,
      title: 'PDF',
      icon: 'file-pdf-box',
    });
  }

  if (program?.method !== 'trial_by_trial') {
    options.push({
      onPress: createCSVFile,
      title: 'CSV',
      icon: 'file-excel',
    });
  }

  return (
    <Animated.View
      style={[
        styles.backgroundColorWhite,
        styles.marginBottom,
        styles.borderRadius,
        animatedStyle,
      ]}>
      <TouchableOpacity
        style={[
          styles.flex,
          styles.row,
          styles.justifySpaceBetween,
          styles.alignCenter,
          styles.padding,
        ]}
        onPress={() => setCollapsed(!collapsed)}>
        <View style={[styles.flex, styles.row]}>
          <View style={[styles.marginLRight]}>
            <ProgramMethodAvatar type={program?.method} />
          </View>
          <View style={[styles.flex, styles.column]}>
            <Text style={[Typography.P3_MEDIUM, styles.textColorPrimary]}>
              {program?.name}
            </Text>
            <Text style={[Typography.CAPTION, styles.textColorSecondary]}>
              {program?.type === 'behavior'
                ? 'Behavior Tracker'
                : 'Skill Acquisition'}{' '}
              |{' '}
              {program?.method === 'interval'
                ? intervalTypes?.find(item => item.value === program?.interval)
                    ?.label
                : method?.title}
            </Text>
          </View>
        </View>
        <View style={[styles.row, styles.alignCenter]}>
          {showProgramMenu ? (
            <>
              <ProgramMenu
                viewInstructions={viewInstructions}
                viewHistory={viewHistory}
              />
              {instructionsModal ? (
                <ProgramInstructionModal
                  show={[instructionsModal, setInstructionsModal]}
                  program={program}
                />
              ) : null}
              {historyModal ? (
                <Modal
                  show={[historyModal, setHistoryModal]}
                  title={'Progress'}>
                  <ProgramProgress program={program} />
                </Modal>
              ) : null}
            </>
          ) : (
            <></>
          )}
          <Menu
            anchor={<AntDesign size={20} name="download" />}
            options={options}
          />
          {showGraphs ? (
            <IconButton
              color={Colors.RAVEN_BLACK}
              icon={'trending-up'}
              onPress={() =>
                navigation.navigate(ProgramProfile.screenName, {
                  programId: program.id,
                  screen: 'Progress',
                })
              }
            />
          ) : (
            <></>
          )}
          <IconButton
            color={Colors.RAVEN_BLACK}
            icon={!collapsed ? 'chevron-up' : 'chevron-down'}
            onPress={() => setCollapsed(!collapsed)}
          />
        </View>
      </TouchableOpacity>
      <Collapsible
        collapsed={collapsed}
        style={[styles.paddingHorizontal, styles.paddingBottom]}
        renderChildrenCollapsed={!collapsed}>
        <ViewShot ref={graphExportRef}>
          {program?.method === 'task_analysis' ? (
            <>
              {showGraphs ? (
                <TaskAnalysisProgressGraph
                  program={
                    useObservables || recentSets?.length > 0
                      ? analyzedProgram()
                      : analysis
                  }
                  sessions={[session]}
                  envs={filteredEnvs}
                />
              ) : (
                <></>
              )}
              <View style={[styles.paddingTop]}>
                <TaskAnalysisTable
                  program={program}
                  sets={sessionSets}
                  taskEvents={
                    useObservables
                      ? []
                      : analyzedEvents.filter(event =>
                          setIds.includes(event.set_id),
                        )
                  }
                  targets={targets}
                  useObservables={useObservables}
                  session={session}
                />
              </View>
            </>
          ) : program?.method === 'trial_by_trial' ? (
            <>
              <TrialByTrialProgress
                program={program}
                patient={patient}
                analysis={analysis}
                sets={sets}
                events={totalEvents}
                recentSets={recentSets?.length > 0 ? recentSets : analyzedSets}
                recentEvents={
                  recentEvents?.length > 0 ? recentEvents : analyzedEvents
                }
                sessions={programSessions}
                session={session}
                targets={targets}
                currentTarget={currentTarget}
                setCurrentTarget={setCurrentTarget}
                table={true}
                showGraphs={showGraphs}
                envs={filteredEnvs}
                sessionMessage={true}
                useObservables={useObservables || recentSets?.length > 0}
                extras={{
                  showExport: true,
                  tableOptions: [
                    {
                      onPress: createCSVFile,
                      title: 'CSV',
                      icon: 'file-excel',
                    },
                  ],
                }}
              />
            </>
          ) : program?.method === 'rate' ? (
            <>
              {showGraphs ? (
                <RateProgressGraph
                  program={
                    useObservables || recentSets?.length > 0
                      ? analyzedProgram()
                      : analysis
                  }
                  sessions={[session]}
                  sets={recentSets}
                  envs={filteredEnvs}
                  useObservables={useObservables}
                />
              ) : (
                <></>
              )}
              <View style={[styles.paddingTop]}>
                <RateTable
                  program={program}
                  session={session}
                  sets={
                    useObservables
                      ? recentSets
                      : analyzedSets.filter(
                          set => set.program_id === program.id,
                        )
                  }
                  events={analyzedEvents}
                />
              </View>
            </>
          ) : program?.method === 'frequency' ? (
            <>
              {showGraphs ? (
                <FrequencyProgressGraph
                  program={
                    useObservables || recentSets?.length > 0
                      ? analyzedProgram()
                      : analysis
                  }
                  sessions={[session]}
                  envs={filteredEnvs}
                />
              ) : (
                <></>
              )}
              <View style={[styles.paddingTop]}>
                <FrequencyTable
                  program={program}
                  sets={
                    useObservables || recentSets?.length > 0
                      ? recentSets
                      : analyzedSets.filter(
                          set => set.program_id === program.id,
                        )
                  }
                  tableEvents={analyzedEvents}
                  useObservables={
                    useObservables || recentSets?.length > 0 ? true : false
                  }
                  session={session}
                />
              </View>
            </>
          ) : program?.method === 'duration' ? (
            <>
              {showGraphs ? (
                <DurationProgressGraph
                  program={
                    useObservables || recentSets?.length > 0
                      ? analyzedProgram()
                      : analysis
                  }
                  sessions={[session]}
                  envs={filteredEnvs}
                />
              ) : (
                <></>
              )}
              <View style={[styles.paddingTop]}>
                <DurationTable
                  program={program}
                  session={session}
                  sets={
                    useObservables || recentSets?.length > 0
                      ? recentSets
                      : analyzedSets.filter(
                          set => set.program_id === program.id,
                        )
                  }
                  tableEvents={analyzedEvents}
                  useObservables={
                    useObservables || recentSets?.length > 0 ? true : false
                  }
                />
              </View>
            </>
          ) : program?.method === 'interval' ? (
            <>
              {showGraphs ? (
                <IntervalProgressGraph
                  program={
                    useObservables || recentSets?.length > 0
                      ? analyzedProgram()
                      : analysis
                  }
                  sessions={[session]}
                  envs={filteredEnvs}
                />
              ) : (
                <></>
              )}
              <View style={[styles.paddingTop]}>
                <IntervalTable
                  program={program}
                  session={session}
                  sets={
                    useObservables || recentSets?.length > 0
                      ? recentSets
                      : analyzedSets.filter(
                          set => set.program_id === program.id,
                        )
                  }
                  events={
                    useObservables || recentSets?.length > 0
                      ? recentEvents
                      : analyzedEvents
                  }
                  useObservables={
                    useObservables || recentSets?.length > 0 ? true : false
                  }
                />
              </View>
            </>
          ) : (
            <></>
          )}
        </ViewShot>
      </Collapsible>
      <Snackbar
        visible={showExportDlg !== undefined}
        onDismiss={() => setShowExportDlg(undefined)}
        duration={3000}
        action={{
          label: 'Dismiss',
          onPress: () => {},
        }}>
        {showExportDlg} was downloaded.
      </Snackbar>
    </Animated.View>
  );
};

export default compose(
  withDatabase,
  withObservables([], ({session, program, database}: any) => {
    const queries = [];
    if (program.type === 'skill') {
      queries.push(Q.where('end_timestamp', Q.notEq(null)));
    }
    return {
      sets: database
        .get('sets')
        .query(
          Q.and(
            Q.where('program_id', program?.id),
            Q.where('deleted_at', null),
            ...queries,
          ),
          Q.sortBy('created_at', Q.asc),
        ),
      recentSets: database
        .get('sets')
        .query(
          Q.and(
            Q.where('session_id', session.id),
            Q.where('program_id', program.id),
            Q.where('deleted_at', null),
          ),
          Q.sortBy('updated_at', Q.asc),
        ),
      patient: program?.patient,
    };
  }),
  withObservables(
    ['sets'],
    ({
      patient,
      sets,
      recentSets,
      program,
      database,
      useObservables = false,
    }: any) => {
      const programSetIds = _.map(sets, 'id');
      const recentSetIds = _.map(recentSets, 'id');
      const sessionIds = _.map(sets, 'sessionId');
      return {
        targets: program?.enabledTargets,
        totalEvents:
          useObservables || programSetIds.length > 0
            ? database
                .get('events')
                .query(
                  Q.where('set_id', Q.oneOf(programSetIds)),
                  Q.where('deleted_at', null),
                  Q.sortBy('created_at', Q.asc),
                )
            : of([]),
        recentEvents:
          useObservables || recentSetIds.length > 0
            ? database
                .get('events')
                .query(
                  Q.where('set_id', Q.oneOf(recentSetIds)),
                  Q.where('deleted_at', null),
                  Q.sortBy('updated_at', Q.asc),
                )
            : of([]),
        envs: patient?.activeEnvironmentalFactors ?? of([]),
        programSessions: database
          .get('sessions')
          .query(Q.where('id', Q.oneOf(sessionIds)), Q.sortBy('date', Q.asc)),
      };
    },
  ),
)(SessionProgramDataTable);
